/**
 * AuditLogTab.tsx — the admin "Audit log" section, rebuilt as the platform
 * BACK-TRACER (G2 clinical governance; UX Wave 3c §6 — CloudTrail/Stripe-
 * events-grade, "it's the back tracer").
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3c (Insights +
 * audit tracer, 2026-07-21), re-read as a RECORD 2026-09-06.
 *
 * WHY: the audit trail is deliberately split across the three OWNING services
 * (orders — approvals/rejections/prescriptions/refunds/compensation; identity
 * — invites/deletions/reset-links/clinician-record edits; clinic — callback
 * lifecycle + handovers + deal moves), each exposing the SAME read shape
 * (GET /v1/{service}/admin/audit). This section fetches the applicable
 * services IN PARALLEL, merges client-side and sorts newest first, so an
 * admin reads one chronological trail without a cross-service join.
 *
 * ── IT IS A RECORD, NOT A TABLE (2026-09-06, CTO) ──────────────────────────
 * The trail used to render as a six-column grid — Time | Actor | Action |
 * Target | Service | Details — and nobody reads a governance trail by column.
 * It now renders as a DAY heading with that day's events listed beneath it,
 * then the previous day, and so on. Each item reads in the order the question
 * is actually asked: WHEN · WHAT HAPPENED · BY WHOM. The actor used to sit
 * between the clock and the event, so no row read as a sentence.
 *
 * THE SERVICE CHIP IS GONE FROM THE ITEM. Which microservice persisted the row
 * is an implementation fact, not a governance one; it stays available in the
 * expanded detail (and the Service filter is untouched), but it no longer
 * competes with the event for the reader's attention.
 *
 * ── ONE ACT, ONE LINE (2026-09-06, CTO: "it says the same thing three times")
 * Nothing de-duplicated the merge, so a human act recorded by more than one
 * owning service appeared once per service — same second, same actor, same
 * wording — and on a real deal that reads as though the clinician approved
 * three times. Identical `(createdAt, actorUserId, action)` triples now
 * collapse into ONE item carrying a quiet "×n ledgers" mark, and the expanded
 * detail still lists every underlying row. See {@link collapseLedgerEchoes}
 * for the two things that deliberately do NOT collapse.
 *
 * ── AUTOMATIC ACTIONS STAY IN THE STREAM (2026-09-06, CTO) ─────────────────
 * Half of what happens to a deal is a server hook — the stage move on
 * approval, the nurture email, a retention sweep. They are MARKED "System",
 * never separated: splitting them out would leave a reader assembling one
 * story from two places, and the interleaving IS the story ("the clinician
 * approved at 10:04, the platform moved the card at 10:04").
 *
 * FILTERS are SERVER-SIDE (2026-07-21 wire change): actor (a person picker
 * fed by the staff-directory read), action (a multi-select over the
 * cross-service vocabulary UNION), target type, origin service and an
 * inclusive date range all ride the query. Each service validates its
 * `action`/`targetType` comma-lists STRICTLY against its OWN vocabulary, so
 * the tracer sends each service only the INTERSECTION — and skips a service
 * entirely when nothing it owns can match (see admin/auditVocab.ts). The bar
 * wears the SHARED toolbar metrics (`CONTROL_ROW_HEIGHT`) — see
 * {@link filterControlStyle} for why the old analytics-density chrome went.
 *
 * PAGINATION is 25/page through the shared ui/Pagination control (Wave 3c
 * aligned the audit page size with every other staff table). The server is
 * cursor-based (`before`, per source), so the page count grows as older
 * entries stream in: while any source still has older rows the control
 * advertises one page beyond what is loaded, and stepping onto it fetches the
 * next slice. Because each source pages independently, only rows at or above
 * the SAFE FRONTIER (the newest "oldest-loaded" timestamp among unexhausted
 * sources) are rendered — a row is shown only once nothing unfetched could
 * still sort above it, so page N never changes under the admin's feet. The
 * de-duplication runs AFTER that filter, which is what makes it safe: a row
 * above the frontier has every twin it will ever have already loaded.
 *
 * Actor ids resolve to display names (fullName, else email) through the
 * batched staff-user lookup — the APIs deliberately return NO actor names
 * (R19-lean rows); an unresolvable id falls back to the raw id. TARGETS
 * (2026-08-26): clinic-service resolves `targetLabel` for assessment-targeted
 * rows on its ADMIN read (a display-time assessment → lead join — the stored
 * rows stay R19-lean); when present it replaces the shortened target id in
 * the collapsed item, and the expanded detail shows it as "Patient" beside
 * the full id. Absent/null (other services, older servers) → the id renders
 * as before. RICH ITEMS: each entry expands in place to the full context the
 * row legitimately holds — actor name + role, the exact UTC timestamp,
 * action, target type + full id, the recorded reason where one exists, and
 * the service of origin. R19 stands: the tracer renders ONLY what the audit
 * row carries — no follow-up reads, no free text beyond the stored reason.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClinicAudit } from '../api/clinic';
import { getIdentityAudit, getStaffUsers, lookupStaffUsers } from '../api/identity';
import { getOrdersAudit } from '../api/orders';
import type { AuditEntry, AuditListResponse, AuditQuery, StaffUserRow } from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { TableSkeleton } from '../ui/skeletons';
import { Pagination, PAGE_SIZE, pageCount, paginate } from '../ui/Pagination';
import { Alert, Badge, Button, CheckboxRow, ErrorState, Help, Select, ChevronDownIcon } from '../ui/kit';
import { CONTROL_ROW_HEIGHT } from '../ui/filters';
import { labelFor } from '../ui/tones';
import { DatePicker } from '../ui/DatePicker';
import {
  AUDIT_ACTIONS,
  AUDIT_ACTION_UNION,
  AUDIT_SOURCES,
  AUDIT_SOURCE_LABELS,
  AUDIT_TARGET_TYPES,
  AUDIT_TARGET_TYPE_UNION,
  type AuditSource,
} from './auditVocab';

const SOURCES: Record<AuditSource, (params: AuditQuery) => Promise<AuditListResponse>> = {
  orders: getOrdersAudit,
  identity: getIdentityAudit,
  clinic: getClinicAudit,
};

/** A target id readable at a glance: emails stay whole, long opaque ids shorten. */
function shortTargetId(targetId: string): string {
  if (targetId.includes('@') || targetId.length <= 12) return targetId;
  return `${targetId.slice(0, 8)}…`;
}

/* ── When the platform acted, not a person ───────────────────────────────── */

/**
 * The id prefix an INTERNAL SERVICE stamps on itself — orders-service sends
 * `svc:gpsclinics-orders-service`, and clinic-service's unattended lanes sign
 * `svc:gpsclinics-clinic-service` (its `SYSTEM_ACTOR_ID`). Colon-namespaced,
 * which an identity-issued userId (a UUID) can never contain, so this test can
 * never match a real person however the row was written.
 */
const SERVICE_PRINCIPAL_PREFIX = 'svc:';

/** The roles a PERSON can hold on this estate; anything else is a machine. */
/**
 * The roles a HUMAN can hold. Anything outside this set is rendered as a
 * system action.
 *
 * ⚠️ THIS LIST MUST TRACK contracts' `Role` ENUM, AND IT DID NOT (found in
 * review, 2026-09-06). It was `['CLINICIAN','ADMIN','PATIENT']` while contracts
 * has declared FOUR roles since the pharmacy lane shipped — `PHARMACY` was
 * missing, so every `order_dispensed`, `order_dispatched` and
 * `pharmacy_auth_succeeded` row rendered with a chip reading "System".
 *
 * That is not cosmetic. Those rows carry a registered pharmacist's own uuid
 * (orders `pharmacy.service.ts` writes `actorRole: 'PHARMACY'`), and the audit
 * trail naming who dispensed a prescription-only medicine is the record a GPhC
 * investigation reads. Marking it "System" tells that reader the platform
 * dispensed the medicine and no professional was involved — the exact
 * inversion the trail exists to prevent.
 *
 * A role added to contracts and not added here is SILENTLY a machine, which is
 * why the failure survived: nothing errors, the rows simply lie. Keep this in
 * step, and prefer widening it over "tidying" an unfamiliar role out.
 */
const PERSON_ROLES: ReadonlySet<string> = new Set(['CLINICIAN', 'ADMIN', 'PATIENT', 'PHARMACY']);

/**
 * Is this row an AUTOMATIC action — a server hook, a scheduler, a sync?
 *
 * ⚠️ THIS IS THE ESTATE'S EXISTING RULE, NOT A NEW CLIENT GUESS. It is exactly
 * clinic-service's `toFeedActorRole` (the agreed portal seam, 2026-08-27):
 * a `svc:` actor id, or a role outside the closed person vocabulary, is
 * SYSTEM. THE ID OVERRIDES THE ROLE deliberately — an internal caller reaches
 * clinic-service through `SchedulerOrAdminGuard`, whose only role vocabulary
 * is PATIENT|CLINICIAN|ADMIN, so those rows carry `ADMIN` as an INGRESS
 * PLACEHOLDER; reading the role alone made the drawer claim a named admin had
 * approved a questionnaire they are forbidden from approving. In a clinical
 * audit that is the dangerous direction — it reads as evidence.
 */
function isAutomatic(entry: AuditEntry): boolean {
  return entry.actorUserId.startsWith(SERVICE_PRINCIPAL_PREFIX) || !PERSON_ROLES.has(entry.actorRole);
}

/** The filter bar's state — '' / [] mean "no filter on this dimension". */
interface Filters {
  actorUserId: string;
  actions: string[];
  targetType: string;
  service: '' | AuditSource;
  /** ISO yyyy-mm-dd from the date fields; sent only once valid. */
  fromDate: string;
  toDate: string;
}

const NO_FILTERS: Filters = { actorUserId: '', actions: [], targetType: '', service: '', fromDate: '', toDate: '' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The services this filter combination can EVER match: the service select
 * narrows directly; an action/target filter excludes any service whose own
 * vocabulary holds none of the chosen values (asking it would be a strict-zod
 * 400, and the answer is an empty page by definition).
 */
function applicableSources(filters: Filters): AuditSource[] {
  return AUDIT_SOURCES.filter((source) => {
    if (filters.service && filters.service !== source) return false;
    if (filters.actions.length > 0 && !filters.actions.some((a) => AUDIT_ACTIONS[source].includes(a))) return false;
    if (filters.targetType && !AUDIT_TARGET_TYPES[source].includes(filters.targetType)) return false;
    return true;
  });
}

/** The query ONE service receives — its own vocabulary intersection only. */
function queryFor(source: AuditSource, filters: Filters, before?: string): AuditQuery {
  const query: AuditQuery = { limit: PAGE_SIZE };
  if (before) query.before = before;
  if (filters.actorUserId) query.actorUserId = filters.actorUserId;
  if (filters.actions.length > 0) query.action = filters.actions.filter((a) => AUDIT_ACTIONS[source].includes(a));
  if (filters.targetType) query.targetType = [filters.targetType];
  // The date fields are inclusive day bounds → whole-day UTC instants.
  if (ISO_DATE.test(filters.fromDate)) query.from = `${filters.fromDate}T00:00:00.000Z`;
  if (ISO_DATE.test(filters.toDate)) query.to = `${filters.toDate}T23:59:59.999Z`;
  return query;
}

interface SourceState {
  items: AuditEntry[];
  status: 'loading' | 'ready' | 'error';
  /** True once a page came back short — no older rows left to ask for. */
  exhausted: boolean;
}

const initialSource: SourceState = { items: [], status: 'loading', exhausted: false };
/** A source the current filters rule out — never asked, nothing to show. */
const inapplicableSource: SourceState = { items: [], status: 'ready', exhausted: true };

/** One merged row: the audit entry + which service it came from. */
interface TracedEntry extends AuditEntry {
  source: AuditSource;
}

/**
 * ONE ACT as the reader meets it: the row that leads the item, plus every
 * ledger row that recorded the same act. `entries[0]` IS `lead`.
 */
interface TracedEvent {
  /** Stable across renders: the leading row's service + audit id. */
  key: string;
  lead: TracedEntry;
  entries: TracedEntry[];
}

/**
 * Collapse the echoes of ONE act into ONE item.
 *
 * The rule is `(createdAt, actorUserId, action)` — the same instant, the same
 * principal, the same word — and TWO deliberate refusals sit on top of it,
 * because over-collapsing an audit trail destroys evidence and there is no
 * undo for that:
 *
 *  1. TWO ROWS FROM THE SAME LEDGER NEVER COLLAPSE, even on an exact triple.
 *     A service that wrote one action twice in a transaction wrote two FACTS
 *     about two different targets — a bulk stage move, or the duplicate pair
 *     (`deal_marked_duplicate` on the copy, `deal_duplicate_absorbed` on the
 *     survivor) — and folding those together would silently delete one.
 *     Cross-ledger is the only case where the same word, at the same instant,
 *     by the same principal is provably ONE act described twice — which is why
 *     the affordance counts LEDGERS and not rows.
 *  2. A GROUP WHERE ANY LEDGER APPEARS TWICE IS LEFT ALONE ENTIRELY. Pairing
 *     "clinic row 2" with "orders row 2" would be a guess about which echo
 *     belongs to which act, and a guess is not an audit trail. Ambiguity
 *     renders exactly as it always did: every row, separately.
 *
 * Different actions that merely share a second are untouched by construction —
 * the action is part of the key. That matters most on approval, where orders
 * writes `order_approved` and clinic's plan-sent hook writes
 * `deal_stage_changed` in the same second: two genuine facts, two items, and
 * the second one marked System.
 *
 * Input order (newest first) is preserved exactly; a collapsed item takes the
 * position of its FIRST row.
 */
export function collapseLedgerEchoes(rows: readonly TracedEntry[]): TracedEvent[] {
  // JSON, not a joined string. `actorUserId` is an OPAQUE principal — a uuid
  // today, but also `svc:gpsclinics-orders-service` and the bare `internal-cron`
  // / `orders-service` shapes clinic-service still writes — so no separator
  // character can be assumed absent from it, and a joiner that turns out to be
  // legal inside one part merges two different acts. JSON escapes its own
  // delimiters, so the key is unambiguous whatever the server sends.
  const keyOf = (entry: TracedEntry) => JSON.stringify([entry.createdAt, entry.actorUserId, entry.action]);

  const groups = new Map<string, TracedEntry[]>();
  for (const entry of rows) {
    const group = groups.get(keyOf(entry));
    if (group) group.push(entry);
    else groups.set(keyOf(entry), [entry]);
  }

  const emitted = new Set<string>();
  const events: TracedEvent[] = [];
  for (const entry of rows) {
    const key = keyOf(entry);
    const group = groups.get(key) ?? [entry];
    const oneRowPerLedger = new Set(group.map((e) => e.source)).size === group.length;
    if (group.length > 1 && oneRowPerLedger) {
      if (emitted.has(key)) continue;
      emitted.add(key);
      events.push({ key: `${group[0].source}-${group[0].auditId}`, lead: group[0], entries: group });
    } else {
      events.push({ key: `${entry.source}-${entry.auditId}`, lead: entry, entries: [entry] });
    }
  }
  return events;
}

/* ── Days and clocks ─────────────────────────────────────────────────────── */

const WEEKDAY_UTC = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' });
const TIME_UTC = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' });

/**
 * The clock an item leads with — "09:04", UTC, 24-hour. The DAY is the heading
 * above it, so repeating the date on every line would be noise. An unparseable
 * instant falls back to the RAW stored string rather than to a blank or a
 * guess: this is the one surface that must show the record exactly as stored.
 */
function utcTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : TIME_UTC.format(date);
}

/** "Thursday 16 July 2026" — the weekday is in because staff ask in weekdays. */
function dayHeading(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${WEEKDAY_UTC.format(date)} ${formatUtcDate(iso)}`;
}

/** One day's worth of the trail, newest day first. */
interface TracedDay {
  heading: string;
  events: TracedEvent[];
}

/** Split an already-sorted (newest-first) run of events into UTC days. */
function groupByDay(events: readonly TracedEvent[]): TracedDay[] {
  const days: TracedDay[] = [];
  for (const event of events) {
    const heading = dayHeading(event.lead.createdAt);
    const current = days[days.length - 1];
    if (current && current.heading === heading) current.events.push(event);
    else days.push({ heading, events: [event] });
  }
  return days;
}

export function AuditLogTab() {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sources, setSources] = useState<Record<AuditSource, SourceState>>({
    orders: initialSource,
    identity: initialSource,
    clinic: initialSource,
  });
  const [page, setPage] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  // The staff directory feeds the ACTOR PICKER (best-effort: on failure the
  // picker simply offers "All actors" — the trail itself is unaffected).
  const [staff, setStaff] = useState<StaffUserRow[]>([]);
  // actorUserId → display name (fullName, else email) from the batched lookup.
  const [actorNames, setActorNames] = useState<Record<string, string>>({});
  const lookedUp = useRef(new Set<string>());

  useEffect(() => {
    getStaffUsers()
      .then(({ users }) => setStaff(users))
      .catch(() => undefined);
  }, []);

  const applicable = useMemo(() => applicableSources(filters), [filters]);

  const loadSource = useCallback((key: AuditSource, params: AuditQuery, append: boolean) => {
    return SOURCES[key](params)
      .then((res) =>
        setSources((prev) => ({
          ...prev,
          [key]: {
            items: append ? [...prev[key].items, ...res.items] : res.items,
            status: 'ready',
            exhausted: res.items.length < PAGE_SIZE,
          },
        })),
      )
      .catch(() => setSources((prev) => ({ ...prev, [key]: { ...prev[key], status: 'error' } })));
  }, []);

  /** (Re)load page 1 of every applicable source under the current filters. */
  const loadInitial = useCallback(() => {
    setPage(1);
    setExpandedKey(null);
    setSources({
      orders: applicable.includes('orders') ? initialSource : inapplicableSource,
      identity: applicable.includes('identity') ? initialSource : inapplicableSource,
      clinic: applicable.includes('clinic') ? initialSource : inapplicableSource,
    });
    for (const key of applicable) void loadSource(key, queryFor(key, filters), false);
  }, [applicable, filters, loadSource]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  // Resolve newly-seen actor ids to display names in ONE batched lookup per
  // change; a failed lookup leaves the raw ids on screen (never an error state).
  //
  // 2026-08-28: this tracer reads the orders/identity/CLINIC audit trails, and
  // clinic rows written by the order-sync carry `svc:gpsclinics-orders-service`
  // as their actor — so this batch met the same whole-body 400 the deal drawer
  // did, and no name resolved anywhere on the tab. `lookupStaffUsers` now drops
  // service principals at the boundary; the raw `svc:` id stays on screen,
  // which is right for a forensic trail (it is self-describing, and this is the
  // one surface that must show the record exactly as stored) — the "System"
  // mark beside it says what that principal IS without overwriting it.
  const allItems = [...sources.orders.items, ...sources.identity.items, ...sources.clinic.items];
  const unresolved = [...new Set(allItems.map((e) => e.actorUserId))].filter((id) => !lookedUp.current.has(id));
  // Key on the id set, not on render count: without a dependency array this
  // effect re-ran after every render, and the old catch-clear below put the
  // ids straight back, so a batch that could only fail was re-fired forever.
  const unresolvedKey = unresolved.join(',');
  useEffect(() => {
    if (unresolved.length === 0) return;
    for (const id of unresolved) lookedUp.current.add(id);
    lookupStaffUsers(unresolved)
      .then(({ users }) => {
        setActorNames((prev) => {
          const next = { ...prev };
          for (const { userId, email, fullName } of users) next[userId] = fullName?.trim() || email;
          return next;
        });
      })
      .catch(() => {
        // Ids stay marked as ATTEMPTED — see DealDrawer.tsx for the full
        // reasoning. The raw ids remain readable meanwhile, and a genuinely
        // new id set (a page change, a filter change) still issues its own
        // batch because the key below changes with it.
      });
    // `unresolved` is a fresh array each render; `unresolvedKey` is its
    // value-equal digest. The directive must be the LAST comment line before the
    // dependency array — a trailing continuation line silently defeats it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedKey]);

  const statuses = applicable.map((key) => sources[key].status);
  const anyLoading = statuses.includes('loading');
  const allFailed = applicable.length > 0 && statuses.every((s) => s === 'error');
  const someFailed = !allFailed && statuses.includes('error');

  // Merge newest-first, tagging each row with its origin service.
  const merged: TracedEntry[] = AUDIT_SOURCES.flatMap((key) => sources[key].items.map((entry) => ({ ...entry, source: key }))).sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );

  // The SAFE FRONTIER: a row renders only once every unexhausted source has
  // been paged at least that far back — otherwise a later fetch could insert
  // newer-than-shown rows and reshuffle earlier pages.
  const unexhausted = applicable.filter((key) => sources[key].status === 'ready' && !sources[key].exhausted && sources[key].items.length > 0);
  const frontier = unexhausted.reduce<string | null>((max, key) => {
    const oldest = sources[key].items.reduce((min, e) => (e.createdAt < min ? e.createdAt : min), sources[key].items[0].createdAt);
    return max === null || oldest > max ? oldest : max;
  }, null);
  const displayable = frontier === null ? merged : merged.filter((e) => e.createdAt >= frontier);

  // ONE ACT, ONE ITEM — and it must run AFTER the frontier filter, never
  // before. A row at or above the frontier is guaranteed to have every twin it
  // will ever have already loaded, so a collapse made there can never be
  // contradicted by a later fetch arriving with the other half of a pair.
  const events = collapseLedgerEchoes(displayable);

  const moreAvailable = unexhausted.length > 0;
  // While older rows exist the control advertises ONE page beyond the loaded
  // items — stepping onto it triggers the next cursor round below.
  const totalForPager = events.length + (moreAvailable ? PAGE_SIZE : 0);

  /** Fetch the next slice from every source that still has older rows. */
  const loadOlder = useCallback(async () => {
    setLoadingOlder(true);
    await Promise.all(
      unexhausted.map((key) => {
        // Each source pages on ITS OWN oldest-seen createdAt (strictly older).
        const oldest = sources[key].items.reduce((min, e) => (e.createdAt < min ? e.createdAt : min), sources[key].items[0].createdAt);
        return loadSource(key, queryFor(key, filters, oldest), true);
      }),
    );
    setLoadingOlder(false);
  }, [unexhausted, sources, filters, loadSource]);

  // Stepping onto a page whose rows are not loaded yet pulls the next slice;
  // this re-runs until either the page fills or every source is exhausted.
  const pageBeyondLoaded = page > pageCount(events.length) && events.length > 0;
  useEffect(() => {
    if (!pageBeyondLoaded || !moreAvailable || loadingOlder || anyLoading) return;
    void loadOlder();
  }, [pageBeyondLoaded, moreAvailable, loadingOlder, anyLoading, loadOlder]);

  // Hold the skeleton until EVERY applicable source answers its first page —
  // rendering a partial merge would reshuffle items as the slower sources land,
  // and would also collapse nothing, because the other half of a pair is
  // exactly what has not arrived yet.
  // Wave 6 §1: row-shaped loading — three blocks per line, the shape the
  // record's "time · what happened · by whom" line actually has.
  if (anyLoading) return <TracerFrame filters={filters} staff={staff} onFilters={setFilters} body={<TableSkeleton rows={6} columns={3} label="Loading the audit log…" />} />;
  if (allFailed) return <TracerFrame filters={filters} staff={staff} onFilters={setFilters} body={<ErrorState message="Sorry — we could not load the audit log." onRetry={loadInitial} />} />;

  const days = groupByDay(paginate(events, page));
  const showFetchingPage = pageBeyondLoaded && moreAvailable;

  return (
    <TracerFrame
      filters={filters}
      staff={staff}
      onFilters={setFilters}
      body={
        <>
          {someFailed ? (
            <Alert tone="warning" role="status">
              Some audit history could not be loaded — the entries below may be incomplete.{' '}
              <button
                type="button"
                onClick={loadInitial}
                style={{ background: 'none', border: 0, padding: 0, color: 'var(--gps-link)', font: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Try again
              </button>
            </Alert>
          ) : null}

          {events.length === 0 ? (
            <Help style={{ marginBottom: 0 }}>No audit entries match these filters.</Help>
          ) : showFetchingPage ? (
            <TableSkeleton rows={4} columns={3} label="Loading older entries…" />
          ) : (
            <section aria-label="Audit log" style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
              {days.map((day) => (
                <TracedDaySection
                  key={day.heading}
                  day={day}
                  actorNames={actorNames}
                  expandedKey={expandedKey}
                  onToggle={(key) => setExpandedKey((current) => (current === key ? null : key))}
                />
              ))}
            </section>
          )}

          <Pagination label="Audit log pages" page={page} total={totalForPager} onPageChange={setPage} />
        </>
      }
    />
  );
}

/** The section chrome: heading + the filter bar over whatever body state. */
function TracerFrame({
  filters,
  staff,
  onFilters,
  body,
}: {
  filters: Filters;
  staff: StaffUserRow[];
  onFilters: (filters: Filters) => void;
  body: React.ReactNode;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      <SectionTitle>Audit log</SectionTitle>
      <FilterBar filters={filters} staff={staff} onChange={onFilters} />
      {body}
    </div>
  );
}

/* ── The filter bar ──────────────────────────────────────────────────────── */

/**
 * ⚠️ EVERY CONTROL ON THIS ROW IS `CONTROL_ROW_HEIGHT`, AND THAT IS THE POINT
 * (2026-09-06, CTO on the Actions trigger sitting short of its neighbours with
 * its chevron wrapped onto a second line).
 *
 * The bar used to wear a local "analytics-density" chrome — 34px controls,
 * 11.5px labels — applied per control, so the ONE control the kit did not draw
 * (the action multi-select) had to restate the metrics by hand, and drifted the
 * moment anything else moved. It also made this the only filter toolbar in the
 * staff console that was not the shared height, and the pipeline bar already
 * proved where that ends: controls at three heights read as three unrelated
 * things however they are aligned (see ui/filters.tsx).
 *
 * So the density override is GONE rather than reproduced: the selects wear the
 * kit's own control metrics, the date fields drop `compact` (their non-compact
 * path is that same 48px kit metric), the labels take the kit `Field`'s 12.5px,
 * and the multi-select trigger is a kit `Button` pinned to the same height.
 * One number, one place, and nothing on the row can fall out of line again.
 */
const filterControlStyle = {
  height: CONTROL_ROW_HEIGHT,
  minHeight: CONTROL_ROW_HEIGHT,
} as const;

/** The kit `Field` label metrics — see the note above on dropping the override. */
const filterLabelStyle = {
  display: 'block',
  fontSize: '12.5px',
  fontWeight: 600,
  color: 'var(--gps-muted)',
  marginBottom: '6px',
} as const;

/** A labelled filter field. Not the kit's `Field`: that carries a bottom margin
 * which would lift every control off the toolbar's baseline. Same binding. */
function FilterField({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} style={filterLabelStyle}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FilterBar({ filters, staff, onChange }: { filters: Filters; staff: StaffUserRow[]; onChange: (filters: Filters) => void }) {
  const set = (partial: Partial<Filters>) => onChange({ ...filters, ...partial });
  const isFiltered =
    filters.actorUserId !== '' ||
    filters.actions.length > 0 ||
    filters.targetType !== '' ||
    filters.service !== '' ||
    filters.fromDate !== '' ||
    filters.toDate !== '';

  return (
    <div
      role="group"
      aria-label="Filter audit entries"
      style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}
    >
      <div style={{ minWidth: '11rem' }}>
        <FilterField label="Actor" htmlFor="audit-filter-actor">
          <Select id="audit-filter-actor" value={filters.actorUserId} onChange={(e) => set({ actorUserId: e.target.value })} style={filterControlStyle}>
            <option value="">All actors</option>
            {staff.map((user) => (
              <option key={user.userId} value={user.userId}>
                {user.fullName?.trim() || user.email}
              </option>
            ))}
          </Select>
        </FilterField>
      </div>

      <ActionMultiSelect selected={filters.actions} onChange={(actions) => set({ actions })} />

      <div style={{ minWidth: '9rem' }}>
        <FilterField label="Target type" htmlFor="audit-filter-target">
          <Select id="audit-filter-target" value={filters.targetType} onChange={(e) => set({ targetType: e.target.value })} style={filterControlStyle}>
            <option value="">All targets</option>
            {AUDIT_TARGET_TYPE_UNION.map((target) => (
              <option key={target} value={target}>
                {labelFor(target)}
              </option>
            ))}
          </Select>
        </FilterField>
      </div>

      <div style={{ minWidth: '8.5rem' }}>
        <FilterField label="Service" htmlFor="audit-filter-service">
          <Select
            id="audit-filter-service"
            value={filters.service}
            onChange={(e) => set({ service: e.target.value as Filters['service'] })}
            style={filterControlStyle}
          >
            <option value="">All services</option>
            {AUDIT_SOURCES.map((source) => (
              <option key={source} value={source}>
                {labelFor(source)}
              </option>
            ))}
          </Select>
        </FilterField>
      </div>

      <div style={{ minWidth: '10rem', maxWidth: '11rem' }}>
        <FilterField label="From" htmlFor="audit-filter-from">
          <DatePicker id="audit-filter-from" value={filters.fromDate} onChange={(fromDate) => set({ fromDate })} />
        </FilterField>
      </div>
      <div style={{ minWidth: '10rem', maxWidth: '11rem' }}>
        <FilterField label="To" htmlFor="audit-filter-to">
          <DatePicker id="audit-filter-to" value={filters.toDate} onChange={(toDate) => set({ toDate })} />
        </FilterField>
      </div>

      {isFiltered ? (
        <Button type="button" variant="ghost" onClick={() => onChange(NO_FILTERS)} style={{ width: 'auto', ...filterControlStyle, padding: '0 18px' }}>
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The ACTION multi-select: a disclosure button over a checkbox list of the
 * cross-service action UNION. A plain labelled group of checkboxes (not a
 * listbox) keeps it screen-reader-simple; the button carries the running
 * count so the collapsed state still says what is filtered.
 *
 * ⚠️ THE TRIGGER IS A KIT `Button`, NOT A HAND-ROLLED ONE (2026-09-06). The old
 * trigger re-implemented the control chrome inline and got two things wrong
 * that only show up beside real neighbours: it was 34px against its selects,
 * and it put the chevron in the button's TEXT FLOW — `ChevronDownIcon` renders
 * `display: block`, so inside an inline button the arrow claimed a line of its
 * own and the control grew a second row. A kit Button is `inline-flex` with a
 * `gap`, so the chevron is a flex item that CANNOT wrap, and the height comes
 * from the shared `CONTROL_ROW_HEIGHT` its neighbours already use.
 *
 * The APPLIED look borrows the shared `FiltersTrigger` palette (tinted fill,
 * primary border, 600 weight) rather than inventing a third one — "this control
 * is on" must read the same on every toolbar in the console. `aria-expanded`
 * still carries the disclosure state, and the PANEL below is untouched.
 */
function ActionMultiSelect({ selected, onChange }: { selected: string[]; onChange: (actions: string[]) => void }) {
  const [open, setOpen] = useState(false);

  function toggle(action: string) {
    onChange(selected.includes(action) ? selected.filter((a) => a !== action) : [...selected, action]);
  }

  return (
    <div style={{ position: 'relative' }}>
      <span id="audit-filter-actions-label" style={filterLabelStyle}>
        Actions
      </span>
      <Button
        type="button"
        variant="ghost"
        aria-expanded={open}
        aria-controls="audit-filter-actions-panel"
        aria-describedby="audit-filter-actions-label"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 'auto',
          ...filterControlStyle,
          padding: '0 16px',
          ...(selected.length > 0
            ? { border: '1px solid var(--gps-primary)', background: 'var(--gps-primary-light)', color: 'var(--gps-text)', fontWeight: 600 }
            : {}),
        }}
      >
        {selected.length === 0 ? 'All actions' : `${selected.length} selected`}
        <ChevronDownIcon size={14} />
      </Button>

      {open ? (
        <div
          id="audit-filter-actions-panel"
          role="group"
          aria-label="Filter by action"
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: '18rem',
            maxHeight: '22rem',
            overflowY: 'auto',
            background: 'var(--gps-surface)',
            border: '1px solid var(--gps-border)',
            borderRadius: 'var(--gps-r-md)',
            boxShadow: 'var(--gps-shadow)',
            padding: '8px 12px',
          }}
        >
          {AUDIT_ACTION_UNION.map((action) => (
            <CheckboxRow key={action} label={labelFor(action)} checked={selected.includes(action)} onChange={() => toggle(action)} />
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--gps-s-2)', paddingTop: '6px' }}>
            <Button type="button" variant="ghost" onClick={() => onChange([])} style={{ width: 'auto', minHeight: '36px', padding: '6px 12px', fontSize: '13px' }}>
              Clear actions
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} style={{ width: 'auto', minHeight: '36px', padding: '6px 12px', fontSize: '13px' }}>
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── The record: a day heading, then that day's events ───────────────────── */

function TracedDaySection({
  day,
  actorNames,
  expandedKey,
  onToggle,
}: {
  day: TracedDay;
  actorNames: Record<string, string>;
  expandedKey: string | null;
  onToggle: (key: string) => void;
}) {
  const headingId = `audit-day-${day.heading.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <h3 id={headingId} style={dayHeadingStyle}>
        {day.heading}
      </h3>
      <ul aria-labelledby={headingId} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {day.events.map((event) => (
          <TracedItem
            key={event.key}
            event={event}
            actorNames={actorNames}
            expanded={expandedKey === event.key}
            onToggle={() => onToggle(event.key)}
          />
        ))}
      </ul>
    </div>
  );
}

/** A microchip glyph for the "System" mark — decorative; the WORD beside it
 * carries the meaning, so the mark is never colour-alone and never icon-alone
 * (WCAG 1.4.1). */
function ChipIcon() {
  return (
    <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true" focusable="false">
      <rect x="4.75" y="4.75" width="6.5" height="6.5" rx="1.2" />
      <path d="M6.5 1.8v2.9M9.5 1.8v2.9M6.5 11.3v2.9M9.5 11.3v2.9M1.8 6.5h2.9M1.8 9.5h2.9M11.3 6.5h2.9M11.3 9.5h2.9" />
    </svg>
  );
}

/**
 * ONE item of the record: **time · what happened · by whom**, in that order.
 *
 * WHY THAT ORDER (2026-09-06, CTO). It used to be time, ACTOR, action — the
 * actor sat between the clock and the event, so no line read as a sentence and
 * the two facts a reader scans for ("what happened", then "who") were split
 * either side of a name of unpredictable length. Leading with the clock and
 * closing with the actor makes each line one readable clause.
 *
 * THE TARGET RIDES WITH THE ACTION, muted, because "Order approved" without
 * "which order" is not a record. It stays the server's own words — the
 * resolved `targetLabel` where clinic-service sent one, else the shortened id.
 */
function TracedItem({
  event,
  actorNames,
  expanded,
  onToggle,
}: {
  event: TracedEvent;
  actorNames: Record<string, string>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { lead, entries } = event;
  const actorName = actorNames[lead.actorUserId] ?? lead.actorUserId;
  const automatic = isAutomatic(lead);
  const ledgerNames = entries.map((e) => AUDIT_SOURCE_LABELS[e.source]).join(', ');

  return (
    <li style={itemStyle}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px' }}>
        <time dateTime={lead.createdAt} style={timeStyle}>
          {utcTime(lead.createdAt)}
        </time>
        <span aria-hidden="true" style={separatorStyle}>
          ·
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px', flex: '1 1 16rem', minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--gps-text)' }}>{labelFor(lead.action)}</span>
          <span style={targetStyle}>
            {labelFor(lead.targetType)} · {lead.targetLabel ?? shortTargetId(lead.targetId)}
          </span>
        </span>
        <span aria-hidden="true" style={separatorStyle}>
          ·
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
          <span style={{ color: 'var(--gps-text)' }}>by {actorName}</span>
          {automatic ? (
            // MARKED, NEVER MOVED (CTO): the server hooks stay interleaved with
            // the human acts, because the interleaving is the story. The raw
            // principal beside it is left exactly as recorded — this mark says
            // what that principal IS, it does not overwrite what was stored.
            <span style={systemChipStyle} title="Automatic — recorded by the platform, not by a person">
              <ChipIcon /> System
            </span>
          ) : null}
          {entries.length > 1 ? (
            <span style={ledgerChipStyle} title={`The same act was recorded in ${entries.length} ledgers: ${ledgerNames}`}>
              ×{entries.length} ledgers
            </span>
          ) : null}
        </span>
        <button type="button" aria-expanded={expanded} onClick={onToggle} style={detailsButtonStyle}>
          Details
        </button>
      </div>

      {expanded ? (
        <div style={{ marginTop: '10px', background: 'var(--gps-primary-light)', borderRadius: 'var(--gps-r-md)', padding: '10px 12px' }}>
          {/* R19: everything below comes off the audit rows themselves — no
              follow-up reads, no free text beyond the recorded reason. */}
          <dl style={detailListStyle}>
            <dt style={detailTermStyle}>Actor</dt>
            <dd style={detailValueStyle}>
              {actorName} <Badge tone="muted">{lead.actorRole}</Badge>
            </dd>
            <dt style={detailTermStyle}>Time (UTC)</dt>
            <dd style={detailValueStyle}>
              {formatUtcDateTime(lead.createdAt)} · <code style={{ font: 'inherit', fontFamily: 'monospace' }}>{lead.createdAt}</code>
            </dd>
            <dt style={detailTermStyle}>Action</dt>
            <dd style={detailValueStyle}>{labelFor(lead.action)}</dd>
            {entries.length === 1 ? <EntryFacts entry={lead} /> : null}
          </dl>

          {/* A collapsed item keeps EVERY ledger's row in full — the collapse is
              a reading aid, never a deletion. One block per ledger, so an
              investigation can still quote the exact row each service holds. */}
          {entries.length > 1
            ? entries.map((entry) => (
                <div key={`${entry.source}-${entry.auditId}`} style={ledgerBlockStyle}>
                  <dl style={detailListStyle}>
                    <EntryFacts entry={entry} />
                  </dl>
                </div>
              ))
            : null}
        </div>
      ) : null}
    </li>
  );
}

/** The per-ledger half of the detail: what THIS service's row records. */
function EntryFacts({ entry }: { entry: TracedEntry }) {
  return (
    <>
      <dt style={detailTermStyle}>Target</dt>
      <dd style={detailValueStyle}>
        {labelFor(entry.targetType)} · <code style={{ font: 'inherit', fontFamily: 'monospace' }}>{entry.targetId}</code>
      </dd>
      {/* The server-resolved patient behind an assessment row (ADMIN read only)
          — rendered beside the FULL id so the two can be quoted together in an
          investigation. */}
      {entry.targetLabel ? (
        <>
          <dt style={detailTermStyle}>Patient</dt>
          <dd style={detailValueStyle}>{entry.targetLabel}</dd>
        </>
      ) : null}
      {entry.reason !== null && entry.reason !== '' ? (
        <>
          <dt style={detailTermStyle}>Reason</dt>
          <dd style={detailValueStyle}>{entry.reason}</dd>
        </>
      ) : null}
      <dt style={detailTermStyle}>Origin</dt>
      <dd style={detailValueStyle}>{AUDIT_SOURCE_LABELS[entry.source]}</dd>
    </>
  );
}

const dayHeadingStyle = {
  margin: '0 0 6px',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  color: 'var(--gps-muted)',
  borderBottom: '1px solid var(--gps-border)',
  paddingBottom: '6px',
} as const;

const itemStyle = {
  padding: '10px 4px',
  borderBottom: '1px solid var(--gps-border)',
  fontSize: '13.5px',
  lineHeight: 1.5,
} as const;

const timeStyle = {
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  color: 'var(--gps-text)',
  whiteSpace: 'nowrap',
} as const;

const separatorStyle = { color: 'var(--gps-border-strong)' } as const;

const targetStyle = { color: 'var(--gps-muted)', minWidth: 0, overflowWrap: 'anywhere' } as const;

const chipBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '1px 8px',
  borderRadius: 'var(--gps-r-pill)',
  fontSize: '11.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
} as const;

const systemChipStyle = { ...chipBase, background: 'var(--gps-surface-2)', color: 'var(--gps-muted)' } as const;

const ledgerChipStyle = { ...chipBase, border: '1px solid var(--gps-border)', color: 'var(--gps-muted)' } as const;

const detailsButtonStyle = {
  marginLeft: 'auto',
  minHeight: '36px',
  padding: '4px 12px',
  borderRadius: 'var(--gps-r-pill)',
  border: '1.5px solid var(--gps-border-strong)',
  background: 'var(--gps-surface)',
  color: 'var(--gps-muted)',
  font: 'inherit',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
} as const;

const detailListStyle = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: '4px var(--gps-s-4)',
  margin: 0,
  fontSize: '13.5px',
} as const;

const ledgerBlockStyle = {
  marginTop: '8px',
  paddingTop: '8px',
  borderTop: '1px solid var(--gps-border)',
} as const;

const detailTermStyle = { fontWeight: 600, color: 'var(--gps-muted)' } as const;
const detailValueStyle = { margin: 0, color: 'var(--gps-text)' } as const;
